import { useState, useCallback } from "react";

import { Calendar, Bell, Trash2, Check } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { cn } from "@/modern/utils";
import { EntityTypeKey } from "@/other/constants/EntityManagerConfig";

import ReminderEditModal from "@/react/components/entityViews/timeline/editors/ReminderEditModal";
import PlanningEditModal, { PlanningFormData } from "@/react/components/entityViews/timeline/editors/PlanningEditModal";
import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { DateCard } from "../types";

const ENTITY_TYPE_FIELD_TO_KEY: Record<string, EntityTypeKey> = {
  taskId: "task",
  clientId: "client",
  resourceId: "resource",
};

/** Parse a date string that may be a single date or a range (e.g. "2020-01-01 to 2025-12-31"). Returns valid Date objects only. */
function parseDateCardDate(dateStr: string): { startDate: Date; endDate: Date; isRange: boolean } {
  const trimmed = (dateStr || "").trim();
  const rangeMatch = trimmed.match(/^(.+?)\s+to\s+(.+)$/i);
  const now = new Date();

  const toValidDate = (s: string): Date => {
    const d = new Date(s.trim());
    return isNaN(d.getTime()) ? new Date(now) : d;
  };

  if (rangeMatch) {
    const start = toValidDate(rangeMatch[1]);
    const end = toValidDate(rangeMatch[2]);
    return { startDate: start, endDate: end > start ? end : start, isRange: true };
  }

  const single = toValidDate(trimmed);
  const end = new Date(single);
  end.setHours(end.getHours() + 1);
  return { startDate: single, endDate: end, isRange: false };
}

interface DateExtractionResultsProps {
  dateCards: DateCard[];
}

interface CardState {
  dismissed: boolean;
  completedPlanning: boolean;
  completedReminder: boolean;
}

export const DateExtractionResults = ({ dateCards }: DateExtractionResultsProps) => {
  const { entityRecord } = useFileDetailModal();
  const recordEntityType = (entityRecord as { entityType?: string }).entityType;
  const entityType: EntityTypeKey = (recordEntityType && ENTITY_TYPE_FIELD_TO_KEY[recordEntityType]) ?? "task";

  const [cardStates, setCardStates] = useState<Record<number, CardState>>(() => {
    const initial: Record<number, CardState> = {};
    dateCards.forEach((_, index) => {
      initial[index] = { dismissed: false, completedPlanning: false, completedReminder: false };
    });
    return initial;
  });

  const [planningModalOpen, setPlanningModalOpen] = useState(false);
  const [planningDefaultValues, setPlanningDefaultValues] = useState<Partial<PlanningFormData> | null>(null);
  const [pendingPlanningCardIndex, setPendingPlanningCardIndex] = useState<number | null>(null);

  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderDefaultValues, setReminderDefaultValues] = useState<Partial<{ alertDate: Date; description: string }> | null>(null);
  const [pendingReminderCardIndex, setPendingReminderCardIndex] = useState<number | null>(null);

  const updateCardState = useCallback((index: number, update: Partial<CardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const handleOpenPlanningModal = useCallback((card: DateCard, index: number) => {
    const { startDate, endDate } = parseDateCardDate(card.date);
    setPlanningDefaultValues({
      startDate,
      endDate,
      name: card.description || `Extracted: ${card.date}`,
    });
    setPendingPlanningCardIndex(index);
    setPlanningModalOpen(true);
  }, []);

  const handleOpenReminderModal = useCallback((card: DateCard, index: number) => {
    const { startDate } = parseDateCardDate(card.date);
    setReminderDefaultValues({
      alertDate: startDate,
      description: card.description || `Extracted date: ${card.date}`,
    });
    setPendingReminderCardIndex(index);
    setReminderModalOpen(true);
  }, []);

  const handlePlanningModalOpenChange = useCallback(
    (open: boolean) => {
      setPlanningModalOpen(open);
      if (!open) setPendingPlanningCardIndex(null);
    },
    []
  );

  const handleReminderModalOpenChange = useCallback(
    (open: boolean) => {
      setReminderModalOpen(open);
      if (!open) setPendingReminderCardIndex(null);
    },
    []
  );

  const handlePlanningSuccess = useCallback(() => {
    if (pendingPlanningCardIndex !== null) {
      updateCardState(pendingPlanningCardIndex, { completedPlanning: true });
      setPendingPlanningCardIndex(null);
    }
  }, [pendingPlanningCardIndex, updateCardState]);

  const handleReminderSuccess = useCallback(() => {
    if (pendingReminderCardIndex !== null) {
      updateCardState(pendingReminderCardIndex, { completedReminder: true });
      setPendingReminderCardIndex(null);
    }
  }, [pendingReminderCardIndex, updateCardState]);

  const handleDismiss = useCallback((index: number) => {
    updateCardState(index, { dismissed: true });
  }, [updateCardState]);

  const formatDate = (dateStr: string): string => {
    try {
      const trimmed = (dateStr || "").trim();
      const rangeMatch = trimmed.match(/^(.+?)\s+to\s+(.+)$/i);
      if (rangeMatch) {
        const start = new Date(rangeMatch[1].trim());
        const end = new Date(rangeMatch[2].trim());
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return dateStr;
        const opts: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "short",
          day: "numeric",
        };
        return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
      }
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const visibleCards = dateCards.filter((_, index) => !cardStates[index]?.dismissed);
  const isTask = entityType === "task";

  if (visibleCards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-sm text-muted-foreground">All date cards have been dismissed.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 p-3">
        <AnimatePresence>
          {dateCards.map((card, index) => {
            const state = cardStates[index];
            if (state?.dismissed) return null;

            return (
              <motion.div
                key={index}
                layout
                initial={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="border border-border rounded-swivu-base overflow-hidden"
              >
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm font-medium text-foreground">{formatDate(card.date)}</span>
                      {card.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{card.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-0.5 shrink-0">
                      {isTask && (
                        <ActionButton
                          icon={Calendar}
                          label="Create Planning Date"
                          completed={state?.completedPlanning}
                          disabled={state?.completedPlanning}
                          onClick={() => handleOpenPlanningModal(card, index)}
                        />
                      )}
                      <ActionButton
                        icon={Bell}
                        label="Create Reminder"
                        completed={state?.completedReminder}
                        disabled={state?.completedReminder}
                        onClick={() => handleOpenReminderModal(card, index)}
                      />
                      <ActionButton
                        icon={Trash2}
                        label="Dismiss"
                        variant="destructive"
                        onClick={() => handleDismiss(index)}
                      />
                    </div>
                  </div>

                  {card.search_text && (
                    <div className="mt-2">
                      <SearchTextLink searchText={card.search_text} />
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {isTask && (
        <PlanningEditModal
          open={planningModalOpen}
          onOpenChange={handlePlanningModalOpenChange}
          mode="create"
          taskId={String(entityRecord.id)}
          defaultFormValues={planningDefaultValues ?? undefined}
          className="z-[--swivu-bryntum-z-index]"
          onSuccess={handlePlanningSuccess}
        />
      )}

      <ReminderEditModal
        open={reminderModalOpen}
        onOpenChange={handleReminderModalOpenChange}
        mode="create"
        entityId={String(entityRecord.id)}
        entityType={entityType}
        defaultFormValues={reminderDefaultValues ?? undefined}
        className="z-[--swivu-bryntum-z-index]"
        onSuccess={handleReminderSuccess}
      />
    </>
  );
};

interface ActionButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  variant?: "default" | "destructive";
  completed?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const ActionButton = ({ icon: Icon, label, variant = "default", completed, disabled, onClick }: ActionButtonProps) => {
  return (
    <button
      type="button"
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150",
        variant === "destructive"
          ? "hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20"
          : "hover:bg-primary/10 hover:text-primary active:bg-primary/20",
        completed && "text-green-600 dark:text-green-400",
        disabled && "opacity-60 cursor-not-allowed pointer-events-none"
      )}
    >
      {completed ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
    </button>
  );
};
